/**
 * FE counterpart of App\Support\McuImsCalculator (live preview).
 * Keep formulas in sync with backend — server is authoritative.
 */

export function hitungImt(tbCm?: number | null, bbKg?: number | null): number | null {
  if (!tbCm || !bbKg || tbCm <= 0 || bbKg <= 0) return null;
  const tbM = tbCm / 100;
  return Math.round((bbKg / (tbM * tbM)) * 100) / 100;
}

export function klasifikasiImt(imt?: number | null): string | null {
  if (imt === null || imt === undefined) return null;
  if (imt < 18.5) return "underweight";
  if (imt < 23.0) return "normal";
  if (imt < 25.0) return "overweight";
  if (imt < 30.0) return "obesitas_1";
  return "obesitas_2";
}

export function klasifikasiTd(sis?: number | null, dia?: number | null): string | null {
  if (sis === null || sis === undefined || dia === null || dia === undefined) return null;
  if (sis < 120 && dia < 80) return "normal";
  if (sis >= 160 || dia >= 100) return "ht_2";
  if (sis >= 140 && dia < 90) return "ht_isolated_sistolik";
  if (sis >= 140 || dia >= 90) return "ht_1";
  return "pre_ht";
}

export function hitungRatioWh(pinggang?: number | null, pinggul?: number | null): number | null {
  if (!pinggang || !pinggul || pinggul <= 0) return null;
  return Math.round((pinggang / pinggul) * 100) / 100;
}

export function hitungPackYear(batangHari?: number | null, lamaTahun?: number | null): number | null {
  if (!batangHari || !lamaTahun) return null;
  return Math.round(((batangHari / 20) * lamaTahun) * 100) / 100;
}

function sumOrNull(items: (number | null | undefined)[]): number | null {
  if (!items || items.length === 0) return null;
  let sum = 0;
  let hasValue = false;
  for (const v of items) {
    if (v === null || v === undefined) continue;
    hasValue = true;
    sum += Number(v) || 0;
  }
  return hasValue ? sum : null;
}

export function skorPhq9(items: (number | null)[]): number | null {
  return sumOrNull(items);
}

export function interpretasiPhq9(skor: number | null): string | null {
  if (skor === null) return null;
  if (skor <= 4) return "minimal";
  if (skor <= 9) return "ringan";
  if (skor <= 14) return "sedang";
  if (skor <= 19) return "sedang_berat";
  return "berat";
}

export function isPhq9SuicideRedFlag(items: (number | null)[]): boolean {
  const item9 = items && items.length >= 9 ? items[8] : null;
  if (item9 === null || item9 === undefined) return false;
  return Number(item9) >= 1;
}

export function skorGad7(items: (number | null)[]): number | null {
  return sumOrNull(items);
}

export function interpretasiGad7(skor: number | null): string | null {
  if (skor === null) return null;
  if (skor <= 4) return "minimal";
  if (skor <= 9) return "ringan";
  if (skor <= 14) return "sedang";
  return "berat";
}

export function skorAuditC(items: (number | null)[]): number | null {
  return sumOrNull(items);
}

export function interpretasiAuditC(skor: number | null, gender: string): string | null {
  if (skor === null) return null;
  const g = (gender || "").toUpperCase();
  const ambang = g === "P" || g === "F" ? 3 : 4;
  return skor >= ambang ? "risiko" : "aman";
}

export function hitungPtaAudiometri(thresholds: Record<number | string, number | string | null | undefined>): number | null {
  const freqs = [500, 1000, 2000, 4000];
  let sum = 0;
  let n = 0;
  for (const hz of freqs) {
    const v = thresholds[hz] ?? thresholds[String(hz)];
    if (v === null || v === undefined || v === "") continue;
    sum += Number(v);
    n++;
  }
  if (n === 0) return null;
  return Math.round((sum / n) * 10) / 10;
}

export function klasifikasiPtaWho(pta: number | null): string | null {
  if (pta === null) return null;
  if (pta < 20) return "normal";
  if (pta < 35) return "ringan";
  if (pta < 50) return "sedang";
  if (pta < 65) return "sedang_berat";
  if (pta < 80) return "berat";
  if (pta < 95) return "sangat_berat";
  return "tuli_total";
}

export function labelKlasifikasiTd(kode: string | null | undefined): string {
  switch (kode) {
    case "normal": return "Normal";
    case "pre_ht": return "Pre-Hipertensi";
    case "ht_1": return "Hipertensi Grade 1";
    case "ht_2": return "Hipertensi Grade 2";
    case "ht_isolated_sistolik": return "Hipertensi Sistolik Terisolasi";
    default: return "-";
  }
}

export function labelKlasifikasiImt(kode: string | null | undefined): string {
  switch (kode) {
    case "underweight": return "Underweight";
    case "normal": return "Normal";
    case "overweight": return "Overweight";
    case "obesitas_1": return "Obesitas I";
    case "obesitas_2": return "Obesitas II";
    default: return "-";
  }
}
